import { useState, useMemo, useEffect, useRef } from 'react';
import { MapContainer, TileLayer, Marker, Popup, useMap } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import { MapPin, Star, Shield, ChevronRight, Lock } from 'lucide-react';
import { Button } from '../components/ui/button';

// ── DONNÉES MOCK PARTENAIRES ──────────────────────────────────────────────────
const PARTNERS = [
  {
    id: 1,
    name: 'Riad El Firdaous',
    category: 'Hébergement adapté',
    city: 'marrakech',
    lat: 31.6278, lng: -7.9974,
    address: 'Derb Sidi Masmoudi, Médina, Marrakech',
    email: 'contact@riad-firdaous.ma',
    rating: 4.8,
    description: 'Riad PMR-friendly au cœur de la médina. Chambres accessibles, hammam adapté, jardins zen.',
    tags: ['PMR', 'Riad', 'Médina'],
    certified: true,
  },
  {
    id: 2,
    name: 'Palmeraie Medical Care',
    category: 'Centre médical',
    city: 'marrakech',
    lat: 31.654, lng: -7.967,
    address: 'Route de la Palmeraie, Marrakech',
    email: 'contact@palmeraie-care.ma',
    rating: 4.6,
    description: 'Centre de soins spécialisé seniors. Équipe soignante francophone disponible 24h/24.',
    tags: ['Médical', 'Soins', '24h/24'],
    certified: true,
  },
  {
    id: 3,
    name: "Les Jardins de l'Agdal",
    category: 'Résidence senior',
    city: 'marrakech',
    lat: 31.6172, lng: -8.01,
    address: "Quartier Agdal, Marrakech",
    email: 'contact@jardins-agdal.ma',
    rating: 4.5,
    description: 'Résidence senior en quartier calme. Accompagnement personnalisé, activités bien-être.',
    tags: ['Résidence', 'Calme', 'Senior'],
    certified: false,
  },
  {
    id: 4,
    name: 'Hôtel Azur Atlantic',
    category: 'Hébergement adapté',
    city: 'agadir',
    lat: 30.4162, lng: -9.6023,
    address: 'Boulevard du 20 Août, Agadir',
    email: 'info@azur-atlantic.ma',
    rating: 4.9,
    description: "Hôtel 5★ en front de mer. Plage accessible, spa adapté, kinésithérapeute sur place.",
    tags: ['PMR', 'Plage', 'Spa'],
    certified: true,
  },
  {
    id: 5,
    name: 'Villa Souss Sérénité',
    category: 'Résidence senior',
    city: 'agadir',
    lat: 30.432, lng: -9.589,
    address: 'Secteur Founty, Agadir',
    email: 'contact@villa-souss.ma',
    rating: 4.7,
    description: "Villa plain-pied avec vue sur l'Atlas, jardin thérapeutique et cuisine diététique.",
    tags: ['Plain-pied', 'Jardin', 'Diété.'],
    certified: true,
  },
  {
    id: 6,
    name: 'Centre Thalassa Agadir',
    category: 'Bien-être & Thalasso',
    city: 'agadir',
    lat: 30.4055, lng: -9.5998,
    address: "Corniche d'Agadir, Agadir",
    email: 'contact@thalassa-agadir.ma',
    rating: 4.4,
    description: 'Thalassothérapie adaptée PMR. Soins marins, balnéothérapie, relaxation.',
    tags: ['Thalasso', 'PMR', 'Relaxation'],
    certified: false,
  },
];

const CITY_COLORS = { marrakech: '#F47B20', agadir: '#5A3B31' };
const CITY_COORDS = {
  all: { center: [31.0, -8.8], zoom: 7 },
  marrakech: { center: [31.6295, -7.9811], zoom: 13 },
  agadir: { center: [30.4278, -9.5981], zoom: 13 },
};

// ── ICÔNES MARQUEURS ──────────────────────────────────────────────────────────
function makePinIcon(city, selected) {
  const color = CITY_COLORS[city];
  const s = selected ? 32 : 24;
  const glow = selected ? `,0 0 0 5px ${color}33` : '';
  return L.divIcon({
    html: `<div style="
      width:${s}px;height:${s}px;
      background:${color};
      border:3px solid #fff;
      border-radius:50% 50% 50% 0;
      transform:rotate(-45deg);
      box-shadow:0 4px 12px rgba(0,0,0,.3)${glow};
      transition:all .2s;
    "></div>`,
    className: '',
    iconSize: [s, s],
    iconAnchor: [s / 2, s],
    popupAnchor: [0, -(s + 4)],
  });
}

// Icônes pré-créées pour éviter la re-création à chaque rendu
const ICONS = {
  marrakech_0: makePinIcon('marrakech', false),
  marrakech_1: makePinIcon('marrakech', true),
  agadir_0: makePinIcon('agadir', false),
  agadir_1: makePinIcon('agadir', true),
};

// ── COMPOSANT DE NAVIGATION CARTE ─────────────────────────────────────────────
function SetViewAny({ target }) {
  const map = useMap();
  const prev = useRef(null);
  useEffect(() => {
    if (!target || target === prev.current) return;
    prev.current = target;
    if (target.type === 'city') {
      map.flyTo(target.center, target.zoom, { duration: 0.9, easeLinearity: 0.3 });
    } else {
      map.flyTo([target.lat, target.lng], 14, { duration: 0.9, easeLinearity: 0.3 });
    }
  }, [target, map]);
  return null;
}

// ── CONTENU POPUP ─────────────────────────────────────────────────────────────
function PopupBody({ p }) {
  const color = CITY_COLORS[p.city];
  return (
    <div style={{ minWidth: 220, maxWidth: 260, fontFamily: 'Inter, sans-serif', padding: '4px 2px' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 8 }}>
        <span style={{ width: 8, height: 8, borderRadius: '50%', background: color, display: 'inline-block', flexShrink: 0 }} />
        <strong style={{ color: '#30251F', fontSize: 14, fontFamily: 'Cormorant Garamond, Georgia, serif', lineHeight: 1.2 }}>
          {p.name}
        </strong>
        {p.certified && (
          <span style={{ fontSize: 10, color: '#F47B20', fontWeight: 700, flexShrink: 0 }}>✓</span>
        )}
      </div>

      <span style={{
        fontSize: 10, padding: '2px 8px', borderRadius: 99,
        background: p.city === 'marrakech' ? '#FFF3E8' : '#F0EBE6',
        color, border: `1px solid ${color}30`,
        display: 'inline-block', marginBottom: 8, fontWeight: 600,
      }}>
        {p.category}
      </span>

      <p style={{ fontSize: 12, color: '#666', lineHeight: 1.6, margin: '0 0 8px 0' }}>
        {p.description}
      </p>

      <div style={{ fontSize: 11, color: '#999', marginBottom: 4 }}>
        📍 {p.address}
      </div>

      <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginTop: 10, paddingTop: 8, borderTop: '1px solid #f0ece8' }}>
        <span style={{ color: '#F47B20', fontSize: 14 }}>★</span>
        <strong style={{ color: '#5A3B31', fontSize: 13 }}>{p.rating}</strong>
        <span style={{ fontSize: 10, color: '#bbb' }}>/ 5.0</span>
        <div style={{ flex: 1 }} />
        {p.tags.slice(0, 2).map(tag => (
          <span key={tag} style={{
            fontSize: 9, padding: '2px 6px', borderRadius: 99,
            background: '#F5F1EE', color: '#888',
          }}>{tag}</span>
        ))}
      </div>
    </div>
  );
}

// ── PAGE PRINCIPALE ───────────────────────────────────────────────────────────
export default function PartnersMapPage() {
  const [selected, setSelected] = useState(null);
  const [cityFilter, setCityFilter] = useState('all');
  const [viewTarget, setViewTarget] = useState(null);

  const filtered = useMemo(() =>
    PARTNERS.filter(p => cityFilter === 'all' || p.city === cityFilter),
    [cityFilter]
  );

  const handleCityFilter = (city) => {
    setCityFilter(city);
    setSelected(null);
    const { center, zoom } = CITY_COORDS[city];
    setViewTarget({ type: 'city', center, zoom, _ts: Date.now() });
  };

  const handlePick = (partner) => {
    if (selected === partner.id) {
      setSelected(null);
    } else {
      setSelected(partner.id);
      setViewTarget({ type: 'partner', lat: partner.lat, lng: partner.lng, _ts: Date.now() });
    }
  };

  const { center, zoom } = CITY_COORDS[cityFilter];

  return (
    <div
      className="flex flex-col overflow-hidden"
      style={{ height: 'calc(100vh - 64px)', marginTop: 64 }}
      data-testid="partners-map-page"
    >
      {/* ── EN-TÊTE ──────────────────────────────────────────────────────────── */}
      <div className="flex-shrink-0 bg-white border-b border-border shadow-sm px-4 sm:px-6 py-3 flex flex-wrap items-center justify-between gap-3 z-10">
        <div>
          <h1 className="font-heading text-xl font-semibold text-primary flex items-center gap-2.5">
            <MapPin className="w-5 h-5 text-accent" aria-hidden="true" />
            Carte des partenaires
          </h1>
          <p className="text-xs text-muted-foreground font-body mt-0.5">
            {PARTNERS.length} partenaires certifiés M-CareLink · Marrakech & Agadir
          </p>
        </div>

        {/* Filtres ville */}
        <div className="flex items-center gap-2 flex-wrap" role="group" aria-label="Filtrer par ville">
          {(['all', 'marrakech', 'agadir']).map((c) => {
            const count = c === 'all' ? PARTNERS.length : PARTNERS.filter(p => p.city === c).length;
            const active = cityFilter === c;
            return (
              <button
                key={c}
                onClick={() => handleCityFilter(c)}
                data-testid={`city-filter-${c}`}
                aria-pressed={active}
                className={`flex items-center gap-1.5 px-4 py-1.5 rounded-full text-sm font-body font-medium transition-all duration-200 ${
                  active
                    ? 'bg-primary text-white shadow-sm'
                    : 'bg-muted text-foreground/70 hover:bg-muted/80 hover:text-foreground'
                }`}
              >
                {c !== 'all' && (
                  <span
                    className="w-2 h-2 rounded-full flex-shrink-0"
                    style={{ background: active ? 'rgba(255,255,255,0.6)' : CITY_COLORS[c] }}
                  />
                )}
                {c === 'all' ? 'Toutes les villes' : c.charAt(0).toUpperCase() + c.slice(1)}
                <span className={`text-[11px] ${active ? 'text-white/60' : 'text-muted-foreground'}`}>
                  ({count})
                </span>
              </button>
            );
          })}
        </div>
      </div>

      {/* ── CONTENU : PANNEAU + CARTE ─────────────────────────────────────────── */}
      <div className="flex flex-col md:flex-row flex-1 overflow-hidden">

        {/* ─ PANNEAU LISTE (gauche desktop / bas mobile) ─ */}
        <aside
          className="order-2 md:order-1 w-full md:w-[360px] md:flex-shrink-0 h-52 md:h-full bg-white border-t md:border-t-0 md:border-r border-border overflow-hidden flex flex-col"
          data-testid="partners-list-panel"
          aria-label="Liste des partenaires"
        >
          {/* Légende */}
          <div className="flex-shrink-0 px-4 py-2.5 bg-[#FBF7F1] border-b border-border/50 flex items-center gap-4">
            <div className="flex items-center gap-1.5">
              <span className="w-2.5 h-2.5 rounded-sm" style={{ background: '#F47B20' }} />
              <span className="text-[11px] font-body text-foreground/70">Marrakech</span>
            </div>
            <div className="flex items-center gap-1.5">
              <span className="w-2.5 h-2.5 rounded-sm" style={{ background: '#5A3B31' }} />
              <span className="text-[11px] font-body text-foreground/70">Agadir</span>
            </div>
            <div className="ml-auto">
              <span className="text-[11px] text-muted-foreground font-body font-medium">
                {filtered.length} résultat{filtered.length !== 1 ? 's' : ''}
              </span>
            </div>
          </div>

          {/* Cartes partenaires */}
          <div className="flex-1 overflow-y-auto divide-y divide-border/40">
            {filtered.map(p => (
              <button
                key={p.id}
                onClick={() => handlePick(p)}
                data-testid={`partner-card-${p.id}`}
                aria-label={`Voir ${p.name} sur la carte`}
                className={`w-full text-left px-4 py-3.5 transition-all duration-200 hover:bg-[#FBF7F1] ${
                  selected === p.id
                    ? 'bg-[#FBF7F1] border-l-[3px] border-accent'
                    : 'border-l-[3px] border-transparent'
                }`}
              >
                <div className="flex items-start gap-3">
                  <div
                    className="mt-1.5 w-2.5 h-2.5 rounded-full flex-shrink-0"
                    style={{ background: CITY_COLORS[p.city] }}
                  />
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-1.5 mb-1">
                      <span className="font-heading text-[14px] font-semibold text-primary truncate">
                        {p.name}
                      </span>
                      {p.certified && (
                        <Shield className="w-3 h-3 text-accent flex-shrink-0" aria-label="Certifié M-CareLink" />
                      )}
                    </div>
                    <div className="flex items-center gap-2 mb-1.5 flex-wrap">
                      <span
                        className="text-[10px] px-2 py-0.5 rounded-full font-body font-medium"
                        style={{
                          background: p.city === 'marrakech' ? '#FFF3E8' : '#F0EBE6',
                          color: CITY_COLORS[p.city],
                        }}
                      >
                        {p.category}
                      </span>
                      <div className="flex items-center gap-0.5">
                        <Star className="w-3 h-3 fill-[#F47B20] text-[#F47B20]" aria-hidden="true" />
                        <span className="text-[11px] font-medium text-foreground/70">{p.rating}</span>
                      </div>
                    </div>
                    <p className="text-[11px] text-muted-foreground font-body line-clamp-2 leading-relaxed">
                      {p.description}
                    </p>
                  </div>
                  <ChevronRight
                    className={`w-3.5 h-3.5 mt-1 flex-shrink-0 transition-transform duration-200 ${
                      selected === p.id ? 'text-accent rotate-90' : 'text-muted-foreground'
                    }`}
                    aria-hidden="true"
                  />
                </div>
              </button>
            ))}
          </div>

          {/* CTA bas de panneau */}
          <div className="flex-shrink-0 p-4 border-t border-border bg-[#FBF7F1]">
            <a href="/devenir-partenaire">
              <Button
                variant="outline"
                size="sm"
                data-testid="become-partner-map-cta"
                className="w-full rounded-full font-body text-sm border-primary/30 text-primary hover:bg-primary hover:text-white transition-colors duration-200"
              >
                Devenir partenaire M-CareLink
              </Button>
            </a>
          </div>
        </aside>

        {/* ─ CARTE INTERACTIVE ─ */}
        <div
          className="order-1 md:order-2 flex-1 relative"
          style={{ minHeight: 0 }}
          data-testid="partners-map"
          aria-label="Carte interactive des partenaires"
        >
          <MapContainer
            center={center}
            zoom={zoom}
            style={{ height: '100%', width: '100%' }}
            scrollWheelZoom={true}
          >
            <TileLayer
              attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
              url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
            />
            <SetViewAny target={viewTarget} />
            {filtered.map(p => (
              <Marker
                key={p.id}
                position={[p.lat, p.lng]}
                icon={ICONS[`${p.city}_${selected === p.id ? 1 : 0}`]}
                eventHandlers={{ click: () => handlePick(p) }}
              >
                <Popup maxWidth={265} closeButton={true}>
                  <PopupBody p={p} />
                </Popup>
              </Marker>
            ))}
          </MapContainer>

          {/* Badge données demo */}
          <div className="absolute bottom-6 right-3 z-[1000] pointer-events-none">
            <span className="bg-white/90 backdrop-blur-sm text-[10px] text-muted-foreground font-body px-3 py-1.5 rounded-full shadow-sm border border-border/30">
              Données de démonstration
            </span>
          </div>

          {/* Badge accès privé */}
          <div className="absolute top-3 right-3 z-[1000] pointer-events-none">
            <span className="flex items-center gap-1.5 bg-primary/90 backdrop-blur-sm text-[11px] text-white font-body font-medium px-3 py-1.5 rounded-full shadow-sm">
              <Lock className="w-3 h-3" aria-hidden="true" />
              Accès réservé aux membres
            </span>
          </div>
        </div>
      </div>
    </div>
  );
}
